import React from 'react';
import { ArrowLeft, Flame, Shield } from 'lucide-react';
import { Button } from './ui/Button';
import { ThemeToggle } from './ui/ThemeToggle';

interface PrivacyPolicyProps {
  onBack?: () => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{title}</h2>
    <div className="text-gray-600 dark:text-gray-300 space-y-3 leading-relaxed">{children}</div>
  </section>
);

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                <span className="text-sm font-medium">Back</span>
              </button>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white">Koru</span>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="mb-10">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Privacy Policy</h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Effective date: January 1, 2025 &nbsp;·&nbsp; Last updated: March 2025
          </p>
          <p className="mt-4 text-gray-600 dark:text-gray-300 leading-relaxed">
            Koru ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy
            explains how we collect, use, and safeguard your information when you use the Koru habit
            tracking application (the "App") on web or mobile platforms.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <Section title="1. Information We Collect">
            <p><strong className="text-gray-800 dark:text-gray-100">Account Information:</strong> When you create an account, we collect your first name, last name, and email address.</p>
            <p><strong className="text-gray-800 dark:text-gray-100">Habit Data:</strong> We store the habits you create, including names, descriptions, icons, colors, frequency settings, and completion records (dates, notes).</p>
            <p><strong className="text-gray-800 dark:text-gray-100">Usage Data:</strong> We may collect information about how you interact with the App, such as feature usage patterns and session timing, to improve the service.</p>
            <p><strong className="text-gray-800 dark:text-gray-100">Device & Technical Data:</strong> We may collect device type, operating system, app version, and crash reports to diagnose issues and improve performance.</p>
            <p><strong className="text-gray-800 dark:text-gray-100">Push Notification Token:</strong> If you enable notifications, we store your device's push notification token to deliver reminders.</p>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Provide, operate, and maintain the App and its core features</li>
              <li>Authenticate your identity and secure your account</li>
              <li>Calculate and display your habit streaks and statistics</li>
              <li>Send push notifications and reminders you have opted into</li>
              <li>Process subscription payments via our payment provider (Stripe)</li>
              <li>Respond to support inquiries and troubleshoot issues</li>
              <li>Analyze usage patterns to improve the App</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>We do <strong className="text-gray-800 dark:text-gray-100">not</strong> sell, rent, or share your personal data with third parties for advertising purposes.</p>
          </Section>

          <Section title="3. Data Storage & Security">
            <p>Your data is stored on secure servers. We implement industry-standard security measures including:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Passwords are hashed using bcrypt before storage — we never store plain-text passwords</li>
              <li>Authentication uses JSON Web Tokens (JWT) transmitted over HTTPS</li>
              <li>Database access is restricted to authorized application services only</li>
              <li>Regular security reviews and dependency updates</li>
            </ul>
            <p>While we take reasonable precautions, no method of transmission over the internet is 100% secure. We encourage you to use a strong, unique password for your account.</p>
          </Section>

          <Section title="4. Third-Party Services">
            <p>We use the following third-party services that may process your data:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong className="text-gray-800 dark:text-gray-100">Stripe</strong> — for processing subscription payments. Stripe's privacy policy applies to payment data. We do not store your card details on our servers.</li>
              <li><strong className="text-gray-800 dark:text-gray-100">Google Sign-In</strong> — if you choose to sign in with Google, Google's privacy policy applies to the authentication process. We only receive your name and email from Google.</li>
              <li><strong className="text-gray-800 dark:text-gray-100">Expo / Firebase Cloud Messaging</strong> — for delivering push notifications to mobile devices.</li>
            </ul>
          </Section>

          <Section title="5. Data Retention">
            <p>We retain your account and habit data for as long as your account is active. If you delete your account, all personal data and habit records associated with your account are permanently deleted from our systems within 30 days.</p>
            <p>Anonymized, aggregated usage statistics may be retained indefinitely as they cannot be linked back to you.</p>
          </Section>

          <Section title="6. Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong className="text-gray-800 dark:text-gray-100">Access</strong> the personal data we hold about you</li>
              <li><strong className="text-gray-800 dark:text-gray-100">Correct</strong> inaccurate data via your profile settings</li>
              <li><strong className="text-gray-800 dark:text-gray-100">Delete</strong> your account and all associated data via the "Delete Account" option in your profile</li>
              <li><strong className="text-gray-800 dark:text-gray-100">Opt out</strong> of push notifications at any time in your notification settings</li>
              <li><strong className="text-gray-800 dark:text-gray-100">Export</strong> your data — contact us and we will provide a copy of your data</li>
            </ul>
            <p>For EU/EEA residents, you also have rights under the GDPR including the right to data portability and to lodge a complaint with your local supervisory authority.</p>
          </Section>

          <Section title="7. Children's Privacy">
            <p>Koru is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such data, please contact us immediately and we will delete it promptly.</p>
          </Section>

          <Section title="8. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the "Last updated" date at the top of this page. Continued use of the App after changes constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="9. Contact Us">
            <p>If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us at:</p>
            <div className="mt-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-100 dark:border-primary-800">
              <p className="font-semibold text-primary-800 dark:text-primary-200">Koru Support</p>
              <p className="text-primary-700 dark:text-primary-300">
                Email:{' '}
                <a
                  href="mailto:support@dailymobilellc.com"
                  className="underline hover:text-primary-900 dark:hover:text-primary-100 transition-colors"
                >
                  support@dailymobilellc.com
                </a>
              </p>
            </div>
          </Section>
        </div>

        {onBack && (
          <div className="mt-8 text-center">
            <Button onClick={onBack} variant="outline" leftIcon={<ArrowLeft size={16} />}>
              Back to Koru
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Koru. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
